import { goSolidApi } from "@/composables/useGoSolidApi";
import { apiErrorResponse, clientErrorMessage } from "@/utils/apiErrors";
import type {
  DetectDevicesResponse,
  Label,
  LabelListResponse,
  UpdateLabelDraftRequest,
} from "./labelsTypes";

/**
 * EKOenergy labels (`/api/ecolabels/labels`). EKOenergy admins (`ekoenergy_admin_enabled`) reach every label;
 * other accounts with ecolabel access reach the labels of their own companies, may draft, edit, delete and
 * reopen them, and download issued PDFs, but issuing, voiding, previewing and a voided label's PDF answer
 * 403. A label of another company answers 404, and an account without ecolabel access gets 403 everywhere.
 * A refusal (4xx) carries a user-worded `message`, read with `clientErrorMessage`, or `pdfErrorMessage` for
 * the two PDF calls.
 */
export const labelsApi = {
  /**
   * `GET /labels`: every label for an admin, the own companies' labels for anyone else; drafts with their
   * rows and problems derived live; sources carry no candidates.
   */
  listLabels: () => goSolidApi.get<LabelListResponse>("/api/ecolabels/labels"),

  /**
   * `GET /labels/<id>`: one label with its sources and, for a source without a linked device, the
   * detected device candidates.
   *
   * @throws 404 for an unknown label or another company's
   */
  getLabel: (labelId: number) =>
    goSolidApi.get<Label>(`/api/ecolabels/labels/${labelId}`),

  /**
   * `DELETE /labels/<id>/sources/<statement_id>`: takes a source off a draft; the statement stays,
   * unlabelled. 200 with the label, or 204 without a body when that was the last source and the draft
   * was deleted with it.
   *
   * @throws 400 when the label is not a draft; 404 for an unknown label or a statement not in it
   */
  removeSource: (labelId: number, statementId: number) =>
    goSolidApi.delete<Label | "">(
      `/api/ecolabels/labels/${labelId}/sources/${statementId}`
    ),

  /**
   * `POST /labels/<id>/sources/<statement_id>/detect`: runs device detection again on what the AI read
   * off the document. Links nothing.
   *
   * @throws 400 when the label is not a draft; 404 for an unknown label or a statement not in it
   */
  detectSourceDevices: (labelId: number, statementId: number) =>
    goSolidApi.post<DetectDevicesResponse>(
      `/api/ecolabels/labels/${labelId}/sources/${statementId}/detect`
    ),

  /**
   * `PUT /labels/<id>`: replaces a draft's name and period.
   *
   * @throws 400 when the label is not a draft or the period is malformed; 404 for an unknown label
   */
  updateLabelDraft: (labelId: number, data: UpdateLabelDraftRequest) =>
    goSolidApi.put<Label>(`/api/ecolabels/labels/${labelId}`, data),

  /**
   * `DELETE /labels/<id>`: deletes a draft and releases its statements; 204 without a body.
   *
   * @throws 400 when the label is not a draft; 404 for an unknown label
   */
  deleteLabelDraft: (labelId: number) =>
    goSolidApi.delete(`/api/ecolabels/labels/${labelId}`),

  /**
   * `POST /labels/<id>/issue`, admins only: numbers a ready draft and freezes its rows.
   *
   * @throws 400 when the label is not a ready draft or its PDF cannot be rendered; 404 for an unknown label
   */
  issueLabel: (labelId: number) =>
    goSolidApi.post<Label>(`/api/ecolabels/labels/${labelId}/issue`),

  /**
   * `POST /labels/<id>/void`, admins only: voids an issued label, which keeps its number and statements.
   *
   * @throws 400 when the label is not issued; 404 for an unknown label
   */
  voidLabel: (labelId: number) =>
    goSolidApi.post<Label>(`/api/ecolabels/labels/${labelId}/void`),

  /**
   * `POST /labels/<id>/reopen`: starts a new draft from a voided label, with its company, name and period,
   * and moves the voided label's statements into it; 201 with the new draft.
   *
   * @throws 400 when the label is not voided, was reopened before, or has no statements left; 404 for an
   *   unknown label or another company's
   */
  reopenLabel: (labelId: number) =>
    goSolidApi.post<Label>(`/api/ecolabels/labels/${labelId}/reopen`),

  /**
   * `GET /labels/<id>/pdf`: an issued label's PDF, a voided one's with the VOID mark, as a Blob attachment.
   *
   * @throws 400 for a draft or a render refusal, its JSON body also a Blob; 403 for a voided label unless the
   *   caller is an admin; 404 for an unknown label
   */
  downloadLabelPdf: (labelId: number) =>
    goSolidApi.get<Blob>(`/api/ecolabels/labels/${labelId}/pdf`, {
      responseType: "blob",
    }),

  /**
   * `GET /labels/<id>/preview`, admins only: a ready draft's PDF with the PREVIEW mark, as a Blob attachment.
   *
   * @throws 400 when the label is not a ready draft or its PDF cannot be rendered, its JSON body also a Blob
   */
  previewLabelPdf: (labelId: number) =>
    goSolidApi.get<Blob>(`/api/ecolabels/labels/${labelId}/preview`, {
      responseType: "blob",
    }),
};

/**
 * `POST /labels/<id>/chat`, the draft's AI chat endpoint for `AiChatCard`: a turn may change the draft's name, period,
 * statement fields and device links, and attached files become new sources. The accepted turn's `result` is the
 * updated `Label`.
 *
 * @throws 400 when the label is not a draft; 404 for an unknown label
 */
export function labelChatEndpoint(labelId: number): string {
  return `/api/ecolabels/labels/${labelId}/chat`;
}

/**
 * `POST /labels/chat`, the `AiChatCard` endpoint that creates a draft: the turn's attached files become its sources,
 * under the seller company sent as `company_id`, and its message is applied to the new draft. The accepted turn's
 * `result` is the created `Label`; its `conversation_id` continues on `labelChatEndpoint` of that label.
 *
 * @throws 400 without a file or with more than 20; 404 for another company, unless the caller is an admin
 */
export function labelCreateChatEndpoint(): string {
  return "/api/ecolabels/labels/chat";
}

/**
 * The message of a refused request (4xx) to one of the PDF calls, whose error body arrives as a Blob
 * because the request asked for one.
 *
 * @returns the backend's message, or null for a failure (5xx, no response) or a body without one
 */
export async function pdfErrorMessage(error: unknown): Promise<string | null> {
  const response = apiErrorResponse(error);
  const body: unknown = response?.data;
  if (response === undefined || !(body instanceof Blob))
    return clientErrorMessage(error);
  if (response.status < 400 || response.status >= 500) return null;

  return messageOfJson(await body.text());
}

function messageOfJson(text: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  const message = (parsed as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0 ? message : null;
}
